import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { flatten } from "../helpers/util";
import { AssignmentStatus } from "../model/model";
import { IPlanningData } from "./../model/interfaces";
import { AssignmentService } from "./assignment.service";
import { GroupService } from "./group.service";
import { LearningObjectiveService } from "./learning-objective.service";
import { LocalstorageService } from "./localstorage-service.service";
import { UserService } from "./user.service";

@Injectable()
export class PlanningService {
  public commandEndpoint = "command-api/Assignment";
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private learningObjectiveService: LearningObjectiveService,
    private assignmentService: AssignmentService,
    private groupService: GroupService,
    private storageService: LocalstorageService
  ) {}

  getPlanningData = (groupid): Observable<IPlanningData> =>
    forkJoin([
      this.userService.get(groupid),
      this.learningObjectiveService.getAll(),
      this.assignmentService.getAssignmentsByGroup(groupid),
      this.groupService.getAll(),
    ]).pipe(
      map(([candidates, learningObjectives, assignments, group]) => {
        const previousSessionData = this.storageService.get(
          `previousSession-${this.userService.user.id}`
        );

        if (previousSessionData?.candidates) {
          candidates = candidates.map((candidate) => {
            const foundCandidate = previousSessionData.candidates.find(
              (_candidate) => _candidate.id === candidate.id
            );
            return {
              ...candidate,
              checked: foundCandidate?.checked ?? true,
            };
          });
        } else {
          candidates = candidates.map((candidate) => {
            return {
              ...candidate,
              checked: true,
            };
          });
        }
        const groupname = group.find((g) => g.groupId === groupid).groupName;
        const grouplist = group.map((g) => {
          return {
            ...g,
            selected: g.groupId === groupid,
          };
        });

        const constructedAssignments = flatten(
          candidates.map((c) =>
            learningObjectives.map((lo) => {
              // try to find assignment. if there is none, create assignement with unassigned
              const matchedAssignment = assignments.find((a) => {
                if (a.candidateId === c.id && a.learningObjectiveId === lo.id) {
                  a.candidateLeft = c.leaver;
                  return true;
                }
                return false;
              });
              return matchedAssignment
                ? matchedAssignment
                : {
                    citoId: lo.citoId,
                    domainId: lo.domainId,
                    domainTitle: lo.domainTitle,
                    groupId: groupid,
                    groupName: groupname,
                    candidateId: c.id,
                    candidateLeft: c.leaver,
                    learningObjectiveId: lo.id,
                    learningObjectiveTitle: lo.title,
                    learningObjectiveTitleCandidate: lo.titleCandidate,
                    status: AssignmentStatus.Unassigned,
                    id: `${lo.id}_${c.id}`,
                    isOffline: lo.offlineTest,
                  };
            })
          )
        );
        return {
          groupname: groupname,
          groups: grouplist,
          candidates: candidates,
          assignments: constructedAssignments,
        };
      })
    );

  plan = (learningObjectiveId: string, candidateId: string) =>
    this.doPlan(learningObjectiveId, candidateId, false);
  unplan = (learningObjectiveId: string, candidateId: string) =>
    this.doPlan(learningObjectiveId, candidateId, true);
  planSelection = (learningObjectiveIds: string[], candidateIds: string[]) =>
    this.doPlanSelection(learningObjectiveIds, candidateIds);
  unplanSelection = (learningObjectiveIds: string[], candidateIds: string[]) =>
    this.doUnplanSelection(learningObjectiveIds, candidateIds);
  planGroup = (
    learningObjectiveId: string,
    groupId: string,
    YearGroup: string
  ) => this.doPlanGroup(learningObjectiveId, groupId, YearGroup, false);
  unplanGroup = (
    learningObjectiveId: string,
    groupId: string,
    YearGroup: string
  ) => this.doPlanGroup(learningObjectiveId, groupId, YearGroup, true);
  markFail = (learningObjectiveId: string, candidateId: string) =>
    this.markCompleted(learningObjectiveId, candidateId, false);
  markPass = (learningObjectiveId: string, candidateId: string) =>
    this.markCompleted(learningObjectiveId, candidateId, true);

  private doPlan = (
    learningObjectiveId: string,
    candidateId: string,
    unplan: boolean
  ) =>
    this.http.post(
      `${this.commandEndpoint}/${unplan ? "UnplanCandidate" : "PlanCandidate"}`,
      {
        teacherId: this.userService.user.id,
        learningObjectiveId: learningObjectiveId,
        candidateId: candidateId,
      },
      { responseType: "text" }
    );

  private doPlanGroup = (
    learningObjectiveId: string,
    groupId: string,
    YearGroup: string,
    unplan: boolean
  ) =>
    this.http.post(
      `${this.commandEndpoint}/${unplan ? "UnplanGroup" : "PlanGroup"}`,
      {
        teacherId: this.userService.user.id,
        learningObjectiveId: learningObjectiveId,
        groupId: groupId,
        YearGroup: YearGroup,
      },
      { responseType: "text" }
    );

  private doPlanSelection = (
    learningObjectiveIds: string[],
    candidateIds: string[]
  ) =>
    this.http.post(
      `${this.commandEndpoint}/PlanCandidatesMultipleLearningObjectives`,
      {
        teacherId: this.userService.user.id,
        userIds: candidateIds,
        learningObjectiveIds: learningObjectiveIds,
      },
      { responseType: "text" }
    );

  private doUnplanSelection = (
    learningObjectiveIds: string[],
    candidateIds: string[]
  ) =>
    this.http.post(
      `${this.commandEndpoint}/UnplanCandidatesMultipleLearningObjectives`,
      {
        teacherId: this.userService.user.id,
        userIds: candidateIds,
        learningObjectiveIds: learningObjectiveIds,
      },
      { responseType: "text" }
    );

  private markCompleted = (
    learningObjectiveId: string,
    candidateId: string,
    passed: boolean
  ) =>
    this.http.post(
      `${this.commandEndpoint}/${
        passed ? "MarkCompletedPass" : "MarkCompletedFail"
      }`,
      {
        teacherId: this.userService.user.id,
        learningObjectiveId: learningObjectiveId,
        candidateId: candidateId,
        passed: passed,
      },
      { responseType: "text" }
    );
}
